import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from 'api';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { queryKeys } from 'shared/constants/queryKeys';
import { ROUTE_PATHS } from 'shared/constants/routes';

import {
  SmartWidgetButtonIcon,
  SmartWidgetCountItem,
  SmartWidgetCountLabel,
  SmartWidgetCountValue,
  SmartWidgetCountsBox,
  SmartWidgetDivider,
  SmartWidgetDot,
  SmartWidgetLoadingBox,
  SmartWidgetLoadingText,
  SmartWidgetPaperSx,
  SmartWidgetScoringText,
  SmartWidgetTitleBox,
  SmartWidgetTitleIcon,
  SmartWidgetTitleText,
  smartWidgetViewButtonSx,
} from '../reports.styles';
import type { ReportFiltersState } from '../types/reports.types';

interface SmartReportWidgetProps {
  startDate: string;
  endDate: string;
  filters?: ReportFiltersState;
}

const COUNT_ITEMS: {
  key: 'good' | 'needsReview' | 'bad' | 'unscored';
  label: string;
  color: string;
}[] = [
  { key: 'good', label: 'Good', color: 'success.main' },
  { key: 'needsReview', label: 'Needs review', color: 'warning.main' },
  { key: 'bad', label: 'Bad', color: 'error.main' },
  { key: 'unscored', label: 'Pending', color: 'text.secondary' },
];

const SmartReportWidget: React.FC<SmartReportWidgetProps> = ({ startDate, endDate, filters }) => {
  const navigate = useNavigate();
  const params = useMemo(() => ({
    from: startDate,
    to: endDate,
    userIds: filters?.userIds?.join(',') || undefined,
    projectIds: filters?.projectIds?.join(',') || undefined,
    clientIds: filters?.clientIds?.join(',') || undefined,
    isBillable: filters?.billable === null ? undefined : filters?.billable,
    hasDescription: filters?.hasDescription,
    descriptionSearch: filters?.descriptionSearch || undefined,
  }), [startDate, endDate, filters]);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.reports.smartSummary(params),
    queryFn: async () => (await reportsService.getSmartSummary(params)).data.data,
    enabled: !!startDate && !!endDate,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // Pending entries get scored in the background — keep this fresh.
    refetchInterval: (query) => ((query.state.data?.totals.unscored ?? 0) > 0 ? 15000 : false),
  });

  const totals = data?.totals;

  const handleOpen = () => {
    navigate(`${ROUTE_PATHS.SMART_REPORT}?from=${startDate}&to=${endDate}`);
  };

  return (
    <Paper variant="outlined" sx={SmartWidgetPaperSx}>
      {/* Title */}
      <SmartWidgetTitleBox>
        <SmartWidgetTitleIcon sx={{ color: 'primary.main' }} />
        <SmartWidgetTitleText>Work-log quality</SmartWidgetTitleText>
      </SmartWidgetTitleBox>

      {/* Divider */}
      <SmartWidgetDivider sx={{ bgcolor: 'divider' }} />

      {isLoading ? (
        <SmartWidgetLoadingBox>
          <CircularProgress size={14} />
          <SmartWidgetLoadingText sx={{ color: 'text.secondary' }}>Loading…</SmartWidgetLoadingText>
        </SmartWidgetLoadingBox>
      ) : (
        <SmartWidgetCountsBox>
          {COUNT_ITEMS.map((item) => (
            <SmartWidgetCountItem key={item.key}>
              <SmartWidgetDot sx={{ bgcolor: item.color }} />
              <SmartWidgetCountValue sx={{ color: item.color }}>
                {totals ? totals[item.key] : 0}
              </SmartWidgetCountValue>
              <SmartWidgetCountLabel sx={{ color: 'text.secondary' }}>{item.label}</SmartWidgetCountLabel>
            </SmartWidgetCountItem>
          ))}

          {!!totals && totals.unscored > 0 && (
            <SmartWidgetScoringText sx={{ color: 'text.secondary' }}>
              AI is still scoring {totals.unscored} {totals.unscored === 1 ? 'entry' : 'entries'}…
            </SmartWidgetScoringText>
          )}
        </SmartWidgetCountsBox>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<SmartWidgetButtonIcon />}
        onClick={handleOpen}
        sx={smartWidgetViewButtonSx}
      >
        View Smart Report
      </Button>
    </Paper>
  );
};

export default SmartReportWidget;
